import { setCsrfTokenForAxios } from './csrf-utils.js';
import axios, { AxiosError } from 'axios';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const csrfTokenResponseSchema = z.object({
  csrfToken: z.string().trim().min(1),
});

export type CsrfContextType = {
  csrfToken: string | null;
  csrfError: unknown | null;
  refreshCsrfToken: () => Promise<void>;
  clearCsrfToken: () => void;
};

const CsrfContext = createContext<CsrfContextType>({
  csrfToken: null,
  csrfError: null,
  refreshCsrfToken: async () => {},
  clearCsrfToken: () => {},
});

export const useCsrf = () => useContext(CsrfContext);

export type CsrfProviderProps = {
  children: ReactNode;
};

export const CsrfProvider: React.FC<CsrfProviderProps> = ({ children }) => {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [csrfError, setCsrfError] = useState<unknown | null>(null);
  const pendingCsrfRequest = useRef<Promise<void> | null>(null);

  const fetchCsrfToken = useCallback(() => {
    // Initial acquisition and login refresh must not race to replace CSRF state.
    if (pendingCsrfRequest.current) return pendingCsrfRequest.current;

    pendingCsrfRequest.current = (async () => {
      try {
        const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000/';
        const url = new URL('/csrf-token', baseURL).href;
        // Use the raw client: fetching a CSRF token must not require authentication.
        const response = await axios.get<unknown>(url, {
          withCredentials: true,
          timeout: 30_000,
        });
        const parsed = csrfTokenResponseSchema.safeParse(response.data);
        if (!parsed.success) {
          throw new AxiosError(
            'Invalid CSRF token response',
            AxiosError.ERR_BAD_RESPONSE,
            response.config,
            response.request,
            response,
          );
        }
        setCsrfToken(parsed.data.csrfToken);
        setCsrfTokenForAxios(parsed.data.csrfToken);
        setCsrfError(null);
      } catch (error) {
        console.error('Failed to fetch CSRF token', error);
        setCsrfError(error);
        throw error;
      }
    })().finally(() => {
      pendingCsrfRequest.current = null;
    });
    return pendingCsrfRequest.current;
  }, []);

  const clearCsrfToken = useCallback(() => {
    setCsrfToken(null);
    setCsrfTokenForAxios(null);
  }, []);

  useEffect(() => {
    void fetchCsrfToken().catch(() => undefined);
  }, [fetchCsrfToken]);

  return (
    <CsrfContext.Provider value={{ csrfToken, csrfError, refreshCsrfToken: fetchCsrfToken, clearCsrfToken }}>
      {children}
    </CsrfContext.Provider>
  );
};
